import PropTypes from "prop-types";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import omit from "lodash/omit";
import { TagIcon } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxLabel,
  ComboboxList,
} from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApplyTagOptions } from "hooks/useApplyTagOptions";
import { useInfiniteScrollSentinel } from "hooks/useInfiniteScrollSentinel";
import { useApplyTagDialog } from "providers/ApplyTagDialogProvider";
import { storeError } from "services/helpers";
import TagService from "services/Tag";

import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";

import AppliedTagItem from "./AppliedTagItem";
import CatalogTagItem from "./CatalogTagItem";
import NextPageStatus from "./NextPageStatus";
import PartlyAppliedTagItem from "./PartlyAppliedTagItem";
import RemovableGroupLabel from "./RemovableGroupLabel";

const EMPTY = [];
const APPLIED_TO_ALL_GROUP = "Applied to all";
const APPLIED_TO_SOME_GROUP = "Applied to some";
const ALL_TAGS_GROUP = "All tags";
const ITEM_BY_GROUP = {
  [APPLIED_TO_ALL_GROUP]: AppliedTagItem,
  [APPLIED_TO_SOME_GROUP]: PartlyAppliedTagItem,
  [ALL_TAGS_GROUP]: CatalogTagItem,
};
const MAX_FLAGS_PER_PAGE = 100;

const groupTitle = (group, checkedCount) => {
  if (group !== APPLIED_TO_ALL_GROUP) return group;
  return checkedCount === 1
    ? "Applied"
    : `Applied to ${checkedCount}/${checkedCount}`;
};

const byName = (a, b) => a.name.localeCompare(b.name);
const reportError = error => storeError(error.message);

const TagsSkeleton = () => (
  <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:p-2" aria-hidden>
    <Skeleton className="tw:h-5 tw:w-full" />
    <Skeleton className="tw:h-5 tw:w-full" />
    <Skeleton className="tw:h-5 tw:w-full" />
  </div>
);

/**
 * Applies tags to, or removes them from, the checked records. The two applied
 * groups are built from the rows themselves; the rest load a page at a time
 * from the server.
 */
const ApplyTagsDropdown = ({
  modelType,
  rows = EMPTY,
  checkedArr = EMPTY,
  accessor = "id",
  onSuccess,
  disabled,
  disabledTooltip,
  iconOnly,
  size,
  variant = "ghost",
  showDisabledTags = false,
  dialSessionId,
  side,
  align,
  className,
}) => {
  const openApplyTagDialog = useApplyTagDialog();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Opening the confirmation closes the popup and clears the search, so the
  // tags are captured when the title is clicked.
  const [tagsToRemove, setTagsToRemove] = useState(null);

  const catalogQuery = useApplyTagOptions({
    modelType,
    search,
    includeDisabled: showDisabledTags,
    dialSessionId,
    enabled: isOpen,
  });
  const catalogTags = catalogQuery.data ?? EMPTY;
  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage: catalogQuery.hasNextPage,
    isFetchingNextPage: catalogQuery.isFetchingNextPage,
    isError: catalogQuery.isFetchNextPageError,
    fetchNextPage: catalogQuery.fetchNextPage,
    loadedCount: catalogTags.length,
  });

  const applyTagMutation = useMutation({
    mutationFn: ({ tag, metadata }) =>
      TagService.addTag(tag, modelType, checkedArr, metadata),
    onSuccess,
  });
  const removeTagsMutation = useMutation({
    mutationFn: tags => TagService.removeTags(tags, modelType, checkedArr),
    onSuccess,
    onError: reportError,
  });

  const rowsById = new Map(
    rows.filter(Boolean).map(row => [row[accessor], row])
  );
  const checkedRows = checkedArr.map(id => rowsById.get(id)).filter(Boolean);

  const countsByTagId = new Map();
  checkedRows.forEach(row =>
    (row.tags ?? []).forEach(tag => {
      const current = countsByTagId.get(tag.id) ?? { tag, count: 0 };
      countsByTagId.set(tag.id, { ...current, count: current.count + 1 });
    })
  );
  const appliedEntries = [...countsByTagId.values()].filter(
    ({ tag }) => tag.auto !== true && (showDisabledTags || tag.enabled)
  );
  const appliedTagIds = appliedEntries.map(({ tag }) => tag.id).sort();

  const flagsQuery = useQuery({
    queryKey: [
      "tags",
      "apply",
      "flags",
      modelType,
      showDisabledTags,
      appliedTagIds,
    ],
    queryFn: () =>
      TagService.getTagsPage({
        model: modelType,
        page: 1,
        perPage: MAX_FLAGS_PER_PAGE,
        includeDisabled: showDisabledTags,
        ids: appliedTagIds,
      }),
    select: ({ data }) => new Map(data.map(tag => [tag.id, tag.has_metadata])),
    enabled: isOpen && appliedTagIds.length > 0,
  });

  // The row's own values belong to that row, not to the tag.
  const appliedTags = appliedEntries.map(({ tag, count }) => ({
    ...omit(tag, ["tag_metadata_values", "tag_metadata_fields"]),
    has_metadata: flagsQuery.data?.get(tag.id),
    count,
  }));
  const fullyApplied = appliedTags.filter(
    ({ count }) => count === checkedArr.length
  );
  const partlyApplied = appliedTags.filter(
    ({ count }) => count < checkedArr.length
  );

  const searchText = search.trim().toLowerCase();
  const appliedIds = new Set(appliedTags.map(tag => tag.id));
  const matchingSorted = tags =>
    tags
      .filter(tag => tag.name.toLowerCase().includes(searchText))
      .sort(byName);
  const unappliedCatalogTags = catalogTags.filter(
    tag => !appliedIds.has(tag.id)
  );

  const groups = [
    { value: APPLIED_TO_ALL_GROUP, items: matchingSorted(fullyApplied) },
    { value: APPLIED_TO_SOME_GROUP, items: matchingSorted(partlyApplied) },
    { value: ALL_TAGS_GROUP, items: unappliedCatalogTags },
  ].filter(group => group.items.length > 0);
  const lastGroup = groups.at(-1)?.value;
  const askToRemoveGroup = value =>
    setTagsToRemove(groups.find(group => group.value === value).items);

  const tagsById = new Map(
    [...catalogTags, ...appliedTags].map(tag => [tag.id, tag])
  );

  // One checked record shows its own metadata values; several start from the
  // tag's defaults.
  const withRowValues = tag => {
    const rowTag =
      checkedRows.length === 1
        ? checkedRows[0].tags?.find(({ id }) => id === tag.id)
        : undefined;
    return rowTag
      ? { ...tag, tag_metadata_values: rowTag.tag_metadata_values }
      : tag;
  };

  const isBusy =
    flagsQuery.isLoading ||
    applyTagMutation.isPending ||
    removeTagsMutation.isPending;

  const pickTag = tag => {
    if (flagsQuery.isError && appliedIds.has(tag.id)) {
      toast.error("Could not apply tags.");
    } else if (tag.has_metadata) {
      openApplyTagDialog(
        withRowValues(tag),
        metadata => applyTagMutation.mutateAsync({ tag, metadata }),
        modelType
      );
    } else {
      applyTagMutation.mutate({ tag }, { onError: reportError });
    }
  };

  // The combobox value is always empty, so the clicked id is the only one.
  const handleValueChange = ([clickedId]) => {
    if (isBusy) return;
    const tag = tagsById.get(clickedId);
    if (fullyApplied.some(({ id }) => id === clickedId)) {
      removeTagsMutation.mutate([tag]);
    } else {
      pickTag(tag);
    }
  };

  const handleOpenChange = open => {
    setIsOpen(open);
    if (!open) setSearch("");
  };

  const isDisabled = Boolean(disabled) || checkedArr.length === 0;
  let tooltipText = null;
  if (isDisabled) tooltipText = disabledTooltip;
  else if (iconOnly) tooltipText = "Apply Tags";

  let emptyContent = "No tags found.";
  if (catalogQuery.isLoading) emptyContent = <TagsSkeleton />;
  else if (catalogQuery.isError) {
    emptyContent = (
      <Button
        type="button"
        variant="ghost"
        size="xs"
        onClick={catalogQuery.refetch}
      >
        Could not load tags. Retry
      </Button>
    );
  }

  const combobox = (
    <Combobox
      items={groups}
      multiple
      value={EMPTY}
      onValueChange={handleValueChange}
      inputValue={search}
      onInputValueChange={setSearch}
      onOpenChange={handleOpenChange}
      filter={null}
      itemToStringLabel={tag => tag.name}
      disabled={isDisabled}
    >
      <ComboboxPrimitive.Trigger
        aria-label="Apply Tags"
        render={
          <Button
            variant={variant}
            size={size ?? (iconOnly ? "icon" : "default")}
            className={cn(iconOnly && "tw:relative", className)}
          />
        }
      >
        <TagIcon data-icon={iconOnly ? undefined : "inline-start"} />
        {!iconOnly && "Apply Tags"}
        {appliedTags.length > 0 && (
          <Badge
            aria-label={`${appliedTags.length} tags applied`}
            className={cn(
              iconOnly &&
                "tw:absolute tw:-top-0.5 tw:-right-0.5 tw:h-4 tw:min-w-4 tw:justify-center tw:rounded-full tw:px-1 tw:text-[10px] tw:tabular-nums tw:overflow-visible"
            )}
          >
            {appliedTags.length}
          </Badge>
        )}
      </ComboboxPrimitive.Trigger>
      <ComboboxContent side={side} align={align} className="tw:w-72">
        <ComboboxInput showTrigger={false} placeholder="Search tags" />
        <ComboboxEmpty>{emptyContent}</ComboboxEmpty>
        <ComboboxList
          aria-busy={isBusy}
          className={cn(
            "tw:scroll-fade",
            isBusy && "tw:pointer-events-none tw:opacity-60"
          )}
        >
          {group => (
            <ComboboxGroup key={group.value} items={group.items}>
              {group.value === ALL_TAGS_GROUP ? (
                <ComboboxLabel>{group.value}</ComboboxLabel>
              ) : (
                <RemovableGroupLabel
                  group={group.value}
                  title={groupTitle(group.value, checkedArr.length)}
                  disabled={isBusy}
                  onRemove={askToRemoveGroup}
                />
              )}
              <ComboboxCollection>
                {tag => {
                  const Item = ITEM_BY_GROUP[group.value];
                  return (
                    <Item
                      key={tag.id}
                      tag={tag}
                      selectedCount={checkedArr.length}
                      isBusy={isBusy}
                      onRemove={tag => removeTagsMutation.mutate([tag])}
                    />
                  );
                }}
              </ComboboxCollection>
              {group.value === lastGroup && (
                <NextPageStatus
                  sentinelRef={sentinelRef}
                  isFetchingNextPage={catalogQuery.isFetchingNextPage}
                  isNextPageError={catalogQuery.isFetchNextPageError}
                  hasLoadedAll={
                    catalogQuery.isSuccess && !catalogQuery.hasNextPage
                  }
                  onRetry={catalogQuery.fetchNextPage}
                />
              )}
            </ComboboxGroup>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );

  return (
    <>
      {tooltipText ? (
        <Tooltip>
          <TooltipTrigger render={<span className="tw:inline-flex" />}>
            {combobox}
          </TooltipTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>
      ) : (
        combobox
      )}
      <ConfirmDeleteDialog
        open={tagsToRemove !== null}
        onOpenChange={open => {
          if (!open) setTagsToRemove(null);
        }}
        title="Remove tags?"
        description={`Remove ${tagsToRemove?.length ?? 0} tags from ${checkedArr.length} records?`}
        confirmText="Remove"
        onConfirm={() => {
          removeTagsMutation.mutate(tagsToRemove);
          setTagsToRemove(null);
        }}
      />
    </>
  );
};

ApplyTagsDropdown.propTypes = {
  modelType: PropTypes.string.isRequired,
  rows: PropTypes.array,
  checkedArr: PropTypes.array,
  accessor: PropTypes.string,
  onSuccess: PropTypes.func,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
  iconOnly: PropTypes.bool,
  variant: PropTypes.oneOf(["ghost", "outline", "secondary"]),
  size: PropTypes.string,
  showDisabledTags: PropTypes.bool,
  dialSessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  side: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  align: PropTypes.oneOf(["start", "center", "end"]),
  className: PropTypes.string,
};

export default ApplyTagsDropdown;
